import React from "react"
import {Routes, Route, useParams, Navigate, useSearchParams} from "react-router-dom"

import {AccountList} from "./AccountList"
import {Account} from "./Account"
import {OutstandingAccountDetails} from "./OutstandingAccountDetails"
import {OtherAccountDetails} from "./OtherAccountDetails"
import {PaymentPlanAccountDetails} from "./PaymentPlanAccountDetails"

import {Customer} from "@indebted/api"

const OutstandingAccountHOC = withAccount(OutstandingAccountDetails)
const OtherAccountHOC = withAccount(OtherAccountDetails)
const PaymentPlanHOC = withAccount(PaymentPlanAccountDetails)

function Accounts({customer, reload}: {customer: Customer; reload: (delayMs?: number) => void}) {
	return (
		<Routes>
			<Route path="/" element={<AccountListWithRedirect customer={customer} reload={reload} />} />
			<Route
				path="/outstanding/:accountID"
				element={<OutstandingAccountHOC accounts={customer.OutstandingAccounts} reload={reload} />}
			/>
			<Route
				path="/payment-plan/:accountID"
				element={<PaymentPlanHOC accounts={customer.PaymentPlanAccounts} reload={reload} />}
			/>
			<Route
				path="/other/:accountID"
				element={<OtherAccountHOC accounts={customer.OtherAccounts} reload={reload} />}
			/>
		</Routes>
	)
}

function AccountListWithRedirect({customer, reload}: {customer: Customer; reload: (delayMs?: number) => void}) {
	const [searchParams] = useSearchParams()
	const accountID = searchParams.get("accountID")

	if (accountID) {
		const subPath = resolveAccountSubPath(customer, accountID)
		if (subPath) {
			const remaining = new URLSearchParams(searchParams)
			remaining.delete("accountID")
			const query = remaining.toString()
			return <Navigate to={`${subPath}${query ? `?${query}` : ""}`} replace />
		}
	}

	return <AccountList customer={customer} reload={reload} />
}

function resolveAccountSubPath(customer: Customer, accountID: string): string | null {
	if (customer.OutstandingAccounts.some((a) => a.ID === accountID)) return `outstanding/${accountID}`
	if (customer.PaymentPlanAccounts.some((a) => a.ID === accountID)) return `payment-plan/${accountID}`
	if (customer.OtherAccounts.some((a) => a.ID === accountID)) return `other/${accountID}`
	return null
}

function withAccount<T extends {ID: string; Closed: boolean}>(
	Details: React.FC<{account: T; reload: (delayMs?: number) => void}>,
) {
	return function AccountDetails({accounts, ...props}: {accounts: T[]; reload: (delayMs?: number) => void}) {
		const {accountID} = useParams()
		const account = accounts.find(({ID}) => ID === accountID)
		if (!account) {
			return <Navigate to=".." replace />
		}

		return <Account account={account} accountDetailsComponent={<Details account={account} {...props} />} />
	}
}

export {Accounts}
